/**
 * Centerline domain objects representing vessel paths and cross-sections.
 */

import { cross, normalize, subtract, distance, squaredDistance, lerp, binarySearchClosest } from './geometry';
import type { Vec3, Mat4, ICenterlineNode, ICenterlinePathData } from './types';

/**
 * Represents a single cross-section along a centerline.
 * Immutable after construction.
 */
export class CenterlineNode implements ICenterlineNode {
    constructor(
        readonly id: number,
        readonly point: Vec3,
        readonly normal: Vec3,
        readonly up: Vec3,
        readonly contour: readonly Vec3[],
        readonly diameter: ICenterlineNode['diameter']
    ) {
        Object.freeze(this);
    }

    /**
     * Get the right vector (cross product of up and normal).
     */
    get right(): Vec3 {
        return cross(this.up, this.normal);
    }

    /**
     * Get the 4x4 transformation matrix for this slice plane.
     * Column-major order, compatible with VTK.js.
     */
    get planeMatrix(): Mat4 {
        const right = this.right;
        return [
            right[0],
            right[1],
            right[2],
            0,
            this.up[0],
            this.up[1],
            this.up[2],
            0,
            this.normal[0],
            this.normal[1],
            this.normal[2],
            0,
            this.point[0],
            this.point[1],
            this.point[2],
            1,
        ];
    }

    /**
     * Create a CenterlineNode from raw API data.
     */
    static fromData(data: ICenterlineNode): CenterlineNode {
        return new CenterlineNode(data.id, data.point, data.normal, data.up, data.contour, data.diameter);
    }
}

/**
 * Interface for a complete centerline path.
 */
export interface ICenterline {
    readonly id: string;
    readonly name: string;
    readonly nodes: readonly CenterlineNode[];
}

/**
 * Result of finding the closest point on a centerline.
 */
export interface ClosestPointResult {
    /** Index of the closest node */
    nodeIndex: number;
    /** Distance along the centerline to this node */
    distance: number;
    /** The closest node's position (projected point) */
    projectedPoint: Vec3;
    /** 3D Euclidean distance from query position to projected point (the "error") */
    error: number;
}

/**
 * Represents a complete centerline path through a vessel.
 */
export class Centerline implements ICenterline {
    private readonly nodeMap: Map<number, CenterlineNode>;
    /** Maps node ID → index within this centerline's nodes array. */
    private readonly nodeIndexById: Map<number, number>;
    private readonly cumulativeDistances: readonly number[];

    constructor(
        readonly id: string,
        readonly name: string,
        readonly nodes: readonly CenterlineNode[]
    ) {
        this.nodeMap = new Map(nodes.map((n) => [n.id, n]));
        this.nodeIndexById = new Map(nodes.map((n, i) => [n.id, i]));
        this.cumulativeDistances = this.computeCumulativeDistances();
        Object.freeze(this.cumulativeDistances);
    }

    /**
     * Total number of nodes.
     */
    get nodeCount(): number {
        return this.nodes.length;
    }

    /**
     * Total length of centerline in mm.
     */
    get totalLength(): number {
        return this.cumulativeDistances[this.cumulativeDistances.length - 1] ?? 0;
    }

    /**
     * Get node at specific index.
     * @throws Error if index is out of bounds
     */
    getNodeAt(index: number): CenterlineNode {
        const node = this.nodes[index];
        if (!node) {
            throw new Error(`Node index ${index} out of bounds [0, ${this.nodeCount})`);
        }
        return node;
    }

    /**
     * Get node by ID.
     */
    getNodeById(id: number): CenterlineNode | undefined {
        return this.nodeMap.get(id);
    }

    /**
     * Get the index of a node within this centerline by its ID.
     * Returns -1 if the node is not part of this centerline.
     */
    getIndexByNodeId(nodeId: number): number {
        return this.nodeIndexById.get(nodeId) ?? -1;
    }

    /**
     * Check whether this centerline contains a node with the given ID.
     */
    hasNode(nodeId: number): boolean {
        return this.nodeIndexById.has(nodeId);
    }

    /**
     * Get distance from start to node at index.
     */
    getDistanceAt(index: number): number {
        return this.cumulativeDistances[index] ?? 0;
    }

    /**
     * Find node index at given distance from start (or from a specific index).
     * @param dist - Distance along the centerline
     * @param fromIndex - Optional starting index to search from (default: 0)
     */
    getIndexAtDistance(dist: number, fromIndex = 0): number {
        const startDistance = this.getDistanceAt(fromIndex);
        const targetDistance = startDistance + dist;
        const clamped = Math.max(0, Math.min(targetDistance, this.totalLength));
        return binarySearchClosest(this.cumulativeDistances, clamped);
    }

    /**
     * Interpolate position at exact distance along the centerline.
     * @param dist - Distance along the centerline
     * @param fromIndex - Optional starting index (default: 0)
     */
    getPositionAtDistance(dist: number, fromIndex = 0): Vec3 {
        const startDistance = this.getDistanceAt(fromIndex);
        const targetDistance = Math.max(0, Math.min(startDistance + dist, this.totalLength));

        // Find the segment containing the target distance
        let segmentStart = 0;
        for (let i = 0; i < this.nodeCount - 1; i++) {
            const segmentEnd = this.cumulativeDistances[i + 1];
            if (targetDistance <= segmentEnd) {
                segmentStart = i;
                break;
            }
            segmentStart = i;
        }

        // Handle edge case: at or beyond the last node
        if (segmentStart >= this.nodeCount - 1) {
            return this.nodes[this.nodeCount - 1].point;
        }

        const nodeDistance = this.getDistanceAt(segmentStart);
        const nextNodeDistance = this.getDistanceAt(segmentStart + 1);
        const segmentLength = nextNodeDistance - nodeDistance;

        if (segmentLength === 0) {
            return this.nodes[segmentStart].point;
        }

        const t = (targetDistance - nodeDistance) / segmentLength;
        return lerp(this.nodes[segmentStart].point, this.nodes[segmentStart + 1].point, t);
    }

    /**
     * Get tangent vector at distance along the centerline.
     * Useful for orienting objects (e.g., stents, catheters) along the vessel.
     * @param dist - Distance along the centerline
     * @param fromIndex - Optional starting index (default: 0)
     */
    getTangentAtDistance(dist: number, fromIndex = 0): Vec3 {
        const startDistance = this.getDistanceAt(fromIndex);
        const targetDistance = startDistance + dist;
        const index = binarySearchClosest(this.cumulativeDistances, targetDistance);

        if (index >= this.nodeCount - 1) {
            return this.nodes[index].normal;
        }

        const p1 = this.nodes[index].point;
        const p2 = this.nodes[index + 1].point;
        const tangent = subtract(p2, p1);
        return normalize(tangent);
    }

    /**
     * Find closest point on centerline to given position.
     * Returns the node index, distance along centerline, projected position, and 3D error.
     */
    findClosestPoint(position: Vec3): ClosestPointResult {
        let closestIndex = 0;
        let closestDistSq = Infinity;

        for (let i = 0; i < this.nodeCount; i++) {
            const distSq = squaredDistance(position, this.nodes[i].point);
            if (distSq < closestDistSq) {
                closestDistSq = distSq;
                closestIndex = i;
            }
        }

        return {
            nodeIndex: closestIndex,
            distance: this.getDistanceAt(closestIndex),
            projectedPoint: this.nodes[closestIndex].point,
            error: Math.sqrt(closestDistSq),
        };
    }

    /**
     * Get the length (distance) between two positions along the centerline.
     * @param startIndex - Starting node index
     * @param endIndex - Ending node index
     * @returns Absolute length between the two positions (always positive)
     */
    getLengthBetween(startIndex: number, endIndex: number): number {
        const startDist = this.getDistanceAt(startIndex);
        const endDist = this.getDistanceAt(endIndex);
        return Math.abs(endDist - startDist);
    }

    /**
     * Get a range of nodes between two distances.
     */
    getNodesInRange(startDistance: number, endDistance: number): readonly CenterlineNode[] {
        const startIndex = this.getIndexAtDistance(startDistance);
        const endIndex = this.getIndexAtDistance(endDistance);
        return this.nodes.slice(startIndex, endIndex + 1);
    }

    /**
     * Compute cumulative distances from start for each node.
     */
    private computeCumulativeDistances(): number[] {
        const distances = [0];
        for (let i = 1; i < this.nodes.length; i++) {
            const prev = this.nodes[i - 1].point;
            const curr = this.nodes[i].point;
            const segmentLength = distance(prev, curr);
            distances.push(distances[i - 1] + segmentLength);
        }
        return distances;
    }

    /**
     * Create a Centerline from raw API data.
     */
    static fromData(pathData: ICenterlinePathData, allNodes: readonly CenterlineNode[]): Centerline {
        const nodes = pathData.nodeIndexes.map((idx) => {
            const node = allNodes[idx];
            if (!node) {
                throw new Error(`Invalid node index ${idx} in path "${pathData.name}"`);
            }
            return node;
        });

        return new Centerline(pathData.name, pathData.name, nodes);
    }
}

/**
 * Factory for creating centerlines from vascular description data.
 */
export class CenterlineFactory {
    /**
     * Create all centerlines from vascular description data.
     */
    static createFromVascularDescription(data: {
        nodes: readonly ICenterlineNode[];
        paths: readonly ICenterlinePathData[];
    }): Map<string, Centerline> {
        // Create all nodes first
        const nodePool = data.nodes.map((n) => CenterlineNode.fromData(n));

        // Create centerlines from paths
        const centerlines = new Map<string, Centerline>();

        for (const path of data.paths) {
            const centerline = Centerline.fromData(path, nodePool);
            centerlines.set(path.name, centerline);
        }

        return centerlines;
    }
}
